import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  LineChart, Line,
  BarChart, Bar,
  AreaChart, Area,
  ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { DIMENSION_LABELS } from "@/lib/scoring";
import EmptyState from "@/components/EmptyState";
import TimeRangeToggle from "@/components/TimeRangeToggle";
import InsightsList from "@/components/InsightsList";
import { todayLocalISO } from "@/lib/calendar";
import { formatAxisDateAR, formatTooltipDateAR } from "@/lib/dates";

// Map avatar zone → preferred correlation pair index
const FOCUS_PAIR = {
  head: "sleep_vs_mood",
  heart: "thoughts_vs_mood",
  legs: "physical_vs_mood",
  sleep: "sleep_vs_mood",
  mood: "thoughts_vs_mood",
  physical: "physical_vs_mood",
  thoughts: "thoughts_vs_mood",
};

const CHART_TYPES = [
  { id: "line", label: "Línea" },
  { id: "bars", label: "Barras" },
  { id: "bars_double", label: "Barras dobles" },
  { id: "area", label: "Área" },
  { id: "area_stacked", label: "Área comparativa" },
];

const COLOR_X = "#2A9D8F";
const COLOR_Y = "#E07A5F";

export default function Correlations() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const focus = params.get("focus");

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [chartType, setChartType] = useState("line");
  const [range, setRange] = useState("30");
  const [insights, setInsights] = useState([]);
  const [insightsMeta, setInsightsMeta] = useState({ learning: false, message: null });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/dashboard/correlations", { params: { days: range, today: todayLocalISO() } }).then(({ data }) => {
      if (!alive) return;
      setPayload(data);
      if (focus) {
        const target = FOCUS_PAIR[focus];
        const idx = data.pairs.findIndex((p) => p.id === target);
        if (idx >= 0) setActive(idx);
      }
    }).finally(() => alive && setLoading(false));
    api.get("/dashboard/insights", { params: { days: range, today: todayLocalISO() } }).then(({ data }) => {
      if (!alive) return;
      setInsights(data.insights || []);
      setInsightsMeta({ learning: !!data.learning, message: data.message });
    });
    return () => { alive = false; };
  }, [range, focus]);

  const validSeries = useMemo(() => {
    if (!payload) return [];
    const pair = payload.pairs[active];
    if (!pair) return [];
    return payload.series.filter((row) => row[pair.x] != null || row[pair.y] != null);
  }, [payload, active]);

  if (loading && !payload) return <div className="py-16 text-center text-ink-600">Cargando…</div>;
  if (!payload) return null;

  if (!validSeries.length) {
    return (
      <div className="space-y-6 animate-fade-up max-w-3xl mx-auto" data-testid="correlations-empty">
        <Header range={range} setRange={setRange} />
        <EmptyState
          title="Aún no podemos detectar patrones"
          description="Necesitamos al menos algunos días de registros con datos en estas dimensiones para mostrarte cómo se relacionan."
          action={
            <button onClick={() => navigate("/log")} className="tk-btn-primary" data-testid="correlations-cta-log">
              Hacer mi registro de hoy
            </button>
          }
        />
      </div>
    );
  }

  const pair = payload.pairs[active];

  return (
    <div className="space-y-6 animate-fade-up" data-testid="correlations-page">
      <Header range={range} setRange={setRange} />

      <section className="space-y-3" data-testid="correlations-insights-section">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h2 className="font-outfit text-xl text-ink-900">Lo que estamos descubriendo</h2>
          <p className="tk-label">Período seleccionado</p>
        </div>
        <InsightsList
          items={insights}
          learning={insightsMeta.learning}
          message={insightsMeta.message}
          emptyHint="Sumá más registros para destrabar más patrones."
        />
      </section>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2" data-testid="correlations-tabs">
          {payload.pairs.map((p, i) => (
            <button
              key={p.id}
              onClick={() => setActive(i)}
              className={`tk-chip ${active === i ? "tk-chip-selected" : ""}`}
              data-testid={`correlation-tab-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded-full bg-sand-100 p-1 self-start sm:self-auto" data-testid="chart-type-toggle">
          {CHART_TYPES.map((t) => (
            <button
              key={t.id}
              onClick={() => setChartType(t.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-dmsans transition ${
                chartType === t.id ? "bg-white text-ink-900 shadow-sm" : "text-ink-600"
              }`}
              data-testid={`chart-type-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="tk-card" data-testid="correlation-chart-card">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="font-outfit text-xl text-ink-900">{pair.label}</h3>
          <div className="flex items-center gap-3 text-xs font-dmsans text-ink-600">
            <Dot color={COLOR_X} /> {DIMENSION_LABELS[pair.x]}
            <Dot color={COLOR_Y} /> {DIMENSION_LABELS[pair.y]}
            <span>· {validSeries.length} días</span>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <CorrelationChart
              type={chartType}
              data={validSeries}
              x={pair.x}
              y={pair.y}
              colorX={COLOR_X}
              colorY={COLOR_Y}
            />
          </ResponsiveContainer>
        </div>
      </div>

      <div className="tk-soft-card">
        <p className="tk-label">Cómo leer este gráfico</p>
        <p className="font-dmsans text-sm text-ink-900 mt-2 leading-relaxed">
          Cuando las series suben y bajan juntas, hay una relación positiva entre las dimensiones.
          Si se mueven en direcciones opuestas, la relación es inversa. Probá distintas visualizaciones para descubrir patrones.
        </p>
      </div>
    </div>
  );
}

function Header({ range, setRange }) {
  return (
    <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="tk-label">Patrones</p>
        <h1 className="font-outfit text-3xl text-ink-900 mt-1">Correlaciones</h1>
        <p className="font-dmsans text-sm text-ink-600 mt-1">
          Descubrí cómo se relacionan tus variables a lo largo del tiempo.
        </p>
      </div>
      <TimeRangeToggle value={range} onChange={setRange} testId="correlations-range" />
    </header>
  );
}

function Dot({ color }) {
  return <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />;
}

const tooltipStyle = { background: "#fff", borderRadius: 12, border: "1px solid #E6E2D8" };
const fmt = (v, n) => [v == null ? "—" : Number(v).toFixed(1), DIMENSION_LABELS[n] || n];

function CorrelationChart({ type, data, x, y, colorX, colorY }) {
  const commonAxes = (
    <>
      <CartesianGrid stroke="#F0EBE0" vertical={false} />
      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6B7A75" }} tickFormatter={formatAxisDateAR} />
      <YAxis domain={[0, 10]} tick={{ fontSize: 10, fill: "#6B7A75" }} />
      <Tooltip contentStyle={tooltipStyle} labelFormatter={formatTooltipDateAR} formatter={fmt} />
      <Legend formatter={(v) => DIMENSION_LABELS[v] || v} wrapperStyle={{ fontFamily: "DM Sans, sans-serif", fontSize: 12 }} />
    </>
  );

  if (type === "bars" || type === "bars_double") {
    return (
      <BarChart data={data} margin={{ top: 10, right: 16, bottom: 4, left: -8 }} barGap={4} barCategoryGap={type === "bars_double" ? 8 : 14}>
        {commonAxes}
        <Bar dataKey={x} fill={colorX} radius={[6, 6, 0, 0]} maxBarSize={type === "bars_double" ? 14 : 22} />
        <Bar dataKey={y} fill={colorY} radius={[6, 6, 0, 0]} maxBarSize={type === "bars_double" ? 14 : 22} />
      </BarChart>
    );
  }

  if (type === "area" || type === "area_stacked") {
    const stackId = type === "area_stacked" ? "a" : undefined;
    return (
      <AreaChart data={data} margin={{ top: 10, right: 16, bottom: 4, left: -8 }}>
        <defs>
          <linearGradient id="grad-x" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colorX} stopOpacity={0.35} />
            <stop offset="100%" stopColor={colorX} stopOpacity={0.05} />
          </linearGradient>
          <linearGradient id="grad-y" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colorY} stopOpacity={0.35} />
            <stop offset="100%" stopColor={colorY} stopOpacity={0.05} />
          </linearGradient>
        </defs>
        {commonAxes}
        <Area type="monotone" dataKey={x} stroke={colorX} strokeWidth={2} fill="url(#grad-x)" stackId={stackId} connectNulls />
        <Area type="monotone" dataKey={y} stroke={colorY} strokeWidth={2} fill="url(#grad-y)" stackId={stackId} connectNulls />
      </AreaChart>
    );
  }

  // line (default)
  return (
    <LineChart data={data} margin={{ top: 10, right: 16, bottom: 4, left: -8 }}>
      {commonAxes}
      <Line type="monotone" dataKey={x} stroke={colorX} strokeWidth={2} dot={{ r: 2.5 }} connectNulls />
      <Line type="monotone" dataKey={y} stroke={colorY} strokeWidth={2} dot={{ r: 2.5 }} connectNulls />
    </LineChart>
  );
}
